import { useState } from "react";
import { Mail, Send, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usuarioService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";

const EsqueceuSenha = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailEnviado, setEmailEnviado] = useState(false);
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();

  const isEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    if (!email.trim()) {
      showError("Por favor, digite seu email");
      return;
    }

    if (!isEmailValid(email)) {
      showError("Por favor, digite um email válido");
      return;
    }

    setLoading(true);

    try {
      await usuarioService.solicitarRedefinicaoSenha({ email: email });
      setEmailEnviado(true);
      showSuccess("Email de redefinição enviado com sucesso!");
    } catch (err) {
      if (
        err.response?.status === 404 ||
        err.message?.includes("não encontrado")
      ) {
        showError("Email não cadastrado no sistema");
      } else {
        showError(err.message || "Erro ao solicitar redefinição de senha");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  const handleTentarNovamente = () => {
    setEmailEnviado(false);
    setEmail("");
  };

  if (emailEnviado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            {/* Ícone de Sucesso */}
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <Mail className="text-green-600" size={40} />
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Email Enviado!
            </h1>

            <p className="text-gray-600 mb-6 leading-relaxed">
              Se o email <strong className="text-gray-800">{email}</strong>{" "}
              estiver cadastrado, você receberá as instruções para redefinir sua
              senha.
            </p>

            <p className="text-sm text-gray-500 mb-8">
              Verifique sua caixa de entrada e spam. O link é válido por 30
              minutos.
            </p>

            <div className="space-y-4">
              <button
                onClick={handleTentarNovamente}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
              >
                Tentar com outro email
              </button>

              <button
                onClick={() => navigate("/login")}
                className="w-full text-blue-600 hover:text-blue-800 font-medium py-2 transition-colors flex items-center justify-center gap-2"
              >
                <ArrowLeft size={16} />
                Voltar ao Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
              <Mail className="text-white" size={30} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Esqueceu sua senha?
            </h1>
            <p className="text-gray-600">
              Digite seu email e enviaremos as instruções para redefinir sua
              senha
            </p>
          </div>

          {/* Formulário */}
          <div className="space-y-6">
            {/* Campo Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Email cadastrado
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="Digite seu email"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Botão de Enviar */}
            <button
              onClick={handleSubmit}
              disabled={loading || !email.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Enviar instruções
                </>
              )}
            </button>
          </div>

          {/* Link para Login */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600 mb-3">Lembrou da sua senha?</p>
            <button
              onClick={() => navigate("/login")}
              className="text-blue-600 hover:text-blue-800 font-medium transition-colors flex items-center justify-center gap-2 mx-auto"
            >
              <ArrowLeft size={16} />
              Voltar ao Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EsqueceuSenha;
